/**
 * Finance utility functions for loan calculations and formatting
 */

/**
 * Finds which APR "tier" the amount falls into
 * @param {number} amount - The loan amount
 * @param {Array<{limit: number, apr: number}>} tiers - Array of APR tiers with limits
 * @returns {number} The applicable APR rate
 */
export function getAprForAmount(amount, tiers) {
  // Early return for edge case
  if (!tiers || tiers.length === 0) {
    return 0;
  }

  // Check if amount is valid
  if (typeof amount !== "number" || isNaN(amount) || amount < 0) {
    console.error("Invalid amount provided to getAprForAmount:", amount);
    return 0;
  }

  try {
    // Find the first tier where amount is less than or equal to the limit
    const matchingTier = tiers.find((tier) => amount <= tier.limit);

    // Return the matching tier's APR or the last tier's APR as default
    return matchingTier ? matchingTier.apr : tiers[tiers.length - 1].apr;
  } catch (error) {
    console.error("Error in getAprForAmount:", error);
    return 0;
  }
}

/**
 * Formats a value as GBP currency (e.g., £300.00)
 * @param {number} value - The monetary value to format
 * @returns {string} Formatted currency string
 */
export function formatMoney(value) {
  // Handle edge cases
  if (value === null || value === undefined || isNaN(value)) {
    return "£0.00";
  }

  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    console.error("Error formatting money value:", error);
    return "£0.00";
  }
}

/**
 * Rounds a number to a specified number of decimal places
 * @param {number} num - The number to round
 * @param {number} decimals - Number of decimal places
 * @returns {number} Rounded number
 */
const round = (num, decimals) => {
  if (isNaN(num)) return 0;
  const factor = Math.pow(10, decimals);
  return Math.round(num * factor) / factor;
};

/**
 * Calculates the effective rate per period as a decimal
 * @param {number} apr - Annual Percentage Rate
 * @param {number} periodFactor - Factor to convert annual rate to period rate
 * @returns {number} Effective rate per period
 */
const calcEffectiveRate = (apr, periodFactor) => {
  // Convert APR to effective period rate
  const rawRate = Math.exp(Math.log(1 + apr / 100) / periodFactor) - 1;
  return round(rawRate, 6);
};

/**
 * Computes the periodic repayment amount using a flat-rate-like calculation
 * to mimic representative examples
 *
 * @param {number} principal - The loan amount
 * @param {number} apr - Annual Percentage Rate
 * @param {number} periods - Number of payment periods
 * @param {string} termType - Either "weeks" or "months"
 * @returns {number} The periodic repayment amount
 */
export function computePaymentNominal(principal, apr, periods, termType) {
  console.log("computePaymentNominal called!", { principal, apr, periods, termType });
  // Coerce inputs to numbers to avoid stringy values sneaking in
  principal = Number(principal);
  apr = Number(apr);
  periods = Number(periods);

  // Validate (apr = 0 is allowed)
  if (
    !Number.isFinite(principal) || principal <= 0 ||
    !Number.isFinite(periods) || periods <= 0 ||
    !Number.isFinite(apr)
  ) {
    console.error("Invalid inputs to computePaymentNominal (APR 0 allowed):", {
      principal, apr, periods, termType,
    });
    return 0;
  }

  // If APR is exactly 0%, just split the principal evenly
  if (apr === 0) {
    return round(principal / periods, 2);
  }

  // Ensure termType is valid
  if (termType !== "weeks" && termType !== "months") {
    termType = "months";
  }

  // Determine period factor: 12 for months, ~365/7 for weeks
  const periodFactor = termType === "weeks" ? 365 / 7 : 12;

  // Effective rate per period (as a decimal)
  const effectiveRate = calcEffectiveRate(apr, periodFactor);

  // Guard for tiny/zero effective rates
  if (effectiveRate <= 0) {
    return round(principal / periods, 2);
  }

  // (1+i)^n
  const factor = Math.pow(1 + effectiveRate, periods);

  // Annuity repayment factor: ((1+i)^n - 1) / (i * (1+i)^n)
  const repaymentFactor = (factor - 1) / (effectiveRate * factor);

  // Payment base
  const paymentBase = principal / repaymentFactor;

  // Annual flat-rate equivalent (kept from your original logic)
  const annualFlatRate =
    ((paymentBase * periods - principal) / (periods * principal)) *
    periodFactor * 100;

  // Periodic repayment using the flat-rate-like formula
  const loanRepayment =
    (principal +
      ((principal * periods) / periodFactor) * (annualFlatRate / 100)) /
    periods;

  return round(loanRepayment, 2);
}

